import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { importTradesFromCSV, createTrade, getAccountsByUser, getAccountGroupsByUser, useQuery, useAction } from "wasp/client/operations";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../client/components/ui/dialog";
import { Button } from "../client/components/ui/button";
import { Input } from "../client/components/ui/input";
import { Textarea } from "../client/components/ui/textarea";
import { Label } from "../client/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../client/components/ui/select";
import { toast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";
import { PenLine, CheckCircle2, XCircle, AlertTriangle, Loader2, ArrowLeft, Upload } from "lucide-react";
import { resolveCommissionForSymbol } from "./symbolCommissions";
import { parseTopstepXFillsPaste } from "./topstepXFillsParser";
import { parseTradovateCSV, type ParsedTradovateTrade, type TradovateParseResult } from "./tradovateCSVParser";
import { previewNetPnl } from "./importPreviewTotal";
import { sanitizeParsedTrades } from "./sanitizeParsedTrades";
import { stripFuturesExpiration } from "../shared/utils";
import { useAccountBlewUpModal } from "../trading-accounts/components/useAccountBlewUpModal";
import { AccountBlewUpModal } from "../trading-accounts/components/AccountBlewUpModal";
import type { AccountViolation } from "./operations";
import { TradeTagSelector } from "./TradeTagSelector";

// Standard Tradovate column order when pasting without header:
// Symbol Qty Buy Price Buy Time Duration Sell Time Sell Price P&L
const TRADOVATE_DEFAULT_IDX = { symbol: 0, qty: 1, buyPrice: 2, buyTime: 3, sellTime: 5, sellPrice: 6, pnl: 7 };

function parseTradovatePaste(text: string): ParsedTradovateTrade[] | null {
  const lines = text.trim().split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length < 1) return null;

  const firstLine = lines[0].toLowerCase();
  const hasHeader = firstLine.includes("symbol") && firstLine.includes("buy price") && firstLine.includes("p&l");

  let idx: typeof TRADOVATE_DEFAULT_IDX;
  let dataStart: number;

  if (hasHeader) {
    const cols = lines[0].split("\t").map((c) => c.trim().toLowerCase());
    idx = {
      symbol: cols.indexOf("symbol"),
      qty: cols.indexOf("qty"),
      buyPrice: cols.indexOf("buy price"),
      buyTime: cols.indexOf("buy time"),
      sellTime: cols.indexOf("sell time"),
      sellPrice: cols.indexOf("sell price"),
      pnl: cols.indexOf("p&l"),
    };
    if (idx.symbol === -1 || idx.buyPrice === -1 || idx.sellPrice === -1 || idx.pnl === -1) return null;
    dataStart = 1;
  } else {
    // No header: assume standard Tradovate column order
    // Validate first line looks like trade data (symbol + numeric prices)
    const cells = lines[0].split("\t").map((c) => c.trim());
    if (cells.length < 7) return null;
    const priceTest = parseFloat(cells[TRADOVATE_DEFAULT_IDX.buyPrice]);
    if (isNaN(priceTest)) return null;
    idx = TRADOVATE_DEFAULT_IDX;
    dataStart = 0;
  }

  const trades: ParsedTradovateTrade[] = [];

  for (let i = dataStart; i < lines.length; i++) {
    const cells = lines[i].split("\t").map((c) => c.trim());
    if (cells.length < 4) continue;

    const rawSymbol = cells[idx.symbol];
    if (!rawSymbol) continue;
    // Tradovate exports dated contracts (MNQM6); align with TopstepX which uses the root (MNQ).
    const symbol = stripFuturesExpiration(rawSymbol);

    const qty = idx.qty !== -1 ? parseFloat(cells[idx.qty]) : 1;
    const buyPrice = parseFloat(cells[idx.buyPrice]);
    const sellPrice = parseFloat(cells[idx.sellPrice]);

    // P&L: accounting notation $(73.00) = -73.00
    const rawPnl = cells[idx.pnl].trim();
    let pnl: number;
    if (/^\$\([\d.,]+\)$/.test(rawPnl)) {
      pnl = -parseFloat(rawPnl.replace(/[$(),]/g, ""));
    } else {
      pnl = parseFloat(rawPnl.replace(/[$,()]/g, ""));
    }

    const parseDateTime = (s: string): Date | null => {
      if (!s) return null;
      const [datePart, timePart] = s.split(" ");
      if (!datePart || !timePart) return null;
      const [month, day, year] = datePart.split("/");
      return new Date(`${year}-${month}-${day}T${timePart}`);
    };

    const buyTime = idx.buyTime !== -1 ? parseDateTime(cells[idx.buyTime]) : null;
    const sellTime = idx.sellTime !== -1 ? parseDateTime(cells[idx.sellTime]) : null;

    if (
      !symbol ||
      isNaN(buyPrice) || isNaN(sellPrice) || isNaN(pnl) ||
      !buyTime || isNaN(buyTime.getTime()) ||
      !sellTime || isNaN(sellTime.getTime())
    ) continue;

    // Detect short: buyTime > sellTime means sell happened first
    const isShort = buyTime > sellTime;
    const entryTime = isShort ? sellTime : buyTime;
    const exitTime = isShort ? buyTime : sellTime;
    const entryPrice = isShort ? sellPrice : buyPrice;
    const exitPrice = isShort ? buyPrice : sellPrice;

    trades.push({
      symbol,
      qty: isNaN(qty) ? 1 : qty,
      entryPrice,
      exitPrice,
      entryTime,
      exitTime,
      pnl,
      type: isShort ? "short" : "long",
    });
  }

  return trades.length > 0 ? trades : null;
}

// ─── TopstepX Paste Parser ────────────────────────────────────────────────────
// Format: 12 lines per trade (vertical, no separator)
// 0: trade ID  1: symbol (/MNQ)  2: qty  3: entry time  4: exit time
// 5: duration  6: entry price  7: exit price  8: pnl  9: fee1  10: fee2  11: direction

function parseTopstepXPaste(text: string): ParsedTradovateTrade[] | null {
  const lines = text.trim().split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length < 12) return null;

  // Quick format check: line 0 should be a long numeric ID, line 11 Long/Short
  if (!/^\d{7,}$/.test(lines[0])) return null;
  if (!/^(long|short)$/i.test(lines[11])) return null;

  const parseTopstepXDate = (s: string): Date | null => {
    // "April 20 2026 @ 4:24:58 pm" → remove "@" → "April 20 2026 4:24:58 pm"
    const clean = s.replace("@", "").replace(/\s+/g, " ").trim();
    const d = new Date(clean);
    return isNaN(d.getTime()) ? null : d;
  };

  const parseMoney = (s: string): number => {
    return parseFloat(s.replace(/[$,]/g, ""));
  };

  const trades: ParsedTradovateTrade[] = [];

  for (let i = 0; i + 11 < lines.length; i += 12) {
    const block = lines.slice(i, i + 12);

    // Validate block structure
    if (!/^\d{7,}$/.test(block[0])) continue;
    if (!/^(long|short)$/i.test(block[11])) continue;

    const symbol = block[1].replace(/^\//, "").toUpperCase();
    const qty = parseInt(block[2], 10);
    const entryTime = parseTopstepXDate(block[3]);
    const exitTime = parseTopstepXDate(block[4]);
    const entryPrice = parseMoney(block[6]);
    const exitPrice = parseMoney(block[7]);
    const pnl = parseMoney(block[8]);
    const fee1 = parseMoney(block[9]);
    const fee2 = parseMoney(block[10]);
    const type = block[11].toLowerCase() as "long" | "short";

    if (
      !symbol ||
      isNaN(qty) ||
      !entryTime || !exitTime ||
      isNaN(entryPrice) || isNaN(exitPrice) || isNaN(pnl) ||
      isNaN(fee1) || isNaN(fee2)
    ) continue;

    trades.push({
      symbol,
      qty,
      entryPrice,
      exitPrice,
      entryTime,
      exitTime,
      pnl,
      type,
      commission: fee1 + fee2,
    });
  }

  return trades.length > 0 ? trades : null;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Step = "choose" | "manual" | "tradovate" | "topstepx";

// "account:ID" or "group:ID" or "none"
type AccountSelection = string;

interface ManualFormData {
  date: string;
  symbol: string;
  type: "long" | "short";
  status: "open" | "closed";
  entryPrice: string;
  exitPrice: string;
  quantity: string;
  pnl: string;
  commission: string;
  notes: string;
  tagIds: string[];
}

const initialManualForm: ManualFormData = {
  date: new Date().toISOString().split("T")[0],
  symbol: "",
  type: "long",
  status: "closed",
  entryPrice: "",
  exitPrice: "",
  quantity: "1",
  pnl: "",
  commission: "0",
  notes: "",
  tagIds: [],
};

interface AddTradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectManual: () => void;
  onImportDone: () => void;
  onImportCSV: () => void;
}

// ─── Account/Group selector value helpers ─────────────────────────────────────

function parseSelection(val: AccountSelection): { type: "none" | "account" | "group"; id: string | null } {
  if (val === "none" || !val) return { type: "none", id: null };
  if (val.startsWith("account:")) return { type: "account", id: val.slice(8) };
  if (val.startsWith("group:")) return { type: "group", id: val.slice(6) };
  return { type: "none", id: null };
}

// ─── Import source <-> account/group eligibility ─────────────────────────────

// An account belongs to Topstep when its effective prop firm template matches Topstep,
// either via the system slug "topstep" or a custom template named "Topstep" (the user
// may have created one before the system seed existed).
function isTopstepTemplate(tpl: any): boolean {
  if (!tpl) return false;
  if (tpl.slug === "topstep") return true;
  return typeof tpl.name === "string" && tpl.name.trim().toLowerCase() === "topstep";
}

function isTopstepAccount(a: any): boolean {
  const tpl = a?.effectiveTemplate ?? a?.propFirmTemplate ?? a?.group?.propFirmTemplate ?? null;
  return isTopstepTemplate(tpl);
}

// An account is "MetaTrader" when it has a MetaAPI connection (synced broker account).
function isMetaTraderAccount(a: any): boolean {
  return a?.metaApiConnection != null;
}

function isTopstepGroup(g: any): boolean {
  return isTopstepTemplate(g?.propFirmTemplate);
}

// ─── Logo with text fallback (same pattern as TradingAccountsPage) ───────────

// Brand logos (Tradovate, Topstep X) are light artwork meant for dark backgrounds,
// so the chip keeps a dark backdrop in both themes. The image is constrained to the
// chip so wide logos scale down instead of overflowing.
function BrandLogoChip({
  src,
  fallback,
  size = "md",
}: {
  src: string;
  fallback: string;
  /** `sm` pour une liste de plateformes supportées, `md` en tête d'un bouton. */
  size?: "sm" | "md";
}) {
  const [failed, setFailed] = useState(false);
  const box = size === "sm" ? "h-7 w-[56px] px-1.5" : "mt-0.5 h-10 w-[72px] px-2";
  const logo = size === "sm" ? "max-h-4" : "max-h-6";
  return (
    <div
      className={`flex flex-shrink-0 items-center justify-center rounded-md bg-[#0F1729] ${box}`}
    >
      {failed ? (
        <span className={`font-semibold text-white ${size === "sm" ? "text-[10px]" : "text-xs"}`}>
          {fallback}
        </span>
      ) : (
        <img
          src={src}
          alt={fallback}
          className={`max-w-full object-contain ${logo}`}
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function AddTradeModal({ open, onOpenChange, onImportDone, onImportCSV = () => {} }: Omit<AddTradeModalProps, "onSelectManual" | "onImportCSV"> & { onSelectManual?: () => void; onImportCSV?: () => void }) {
  const t = useTranslation();
  const [step, setStep] = useState<Step>("choose");
  const [pasteText, setPasteText] = useState("");
  const [parsedTrades, setParsedTrades] = useState<ParsedTradovateTrade[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [unmatchedFills, setUnmatchedFills] = useState(0);
  const [accountSelection, setAccountSelection] = useState<AccountSelection>("none");
  const [commissionPerTrade, setCommissionPerTrade] = useState<string>(
    () => localStorage.getItem("tradovate_commission_per_trade") ?? "1.04"
  );
  const [isImporting, setIsImporting] = useState(false);
  const [manualForm, setManualForm] = useState<ManualFormData>(initialManualForm);
  const [isSubmittingManual, setIsSubmittingManual] = useState(false);
  const { handleViolation, handleViolations, blewUpModalProps } = useAccountBlewUpModal();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const importTradesFn = useAction(importTradesFromCSV);
  const createTradeFn = useAction(createTrade);
  const { data: accountsData } = useQuery(getAccountsByUser);
  const accounts = (accountsData as any)?.accounts ?? [];
  const { data: groups } = useQuery(getAccountGroupsByUser);
  const isAccountActive = (a: any) => a.status !== "inactive" && a.isActive !== false;
  const accountsList = accounts.filter(isAccountActive);
  const allAccountsList = accounts;
  const groupsList = (Array.isArray(groups) ? groups : []).filter((g: any) =>
    allAccountsList.some((a: any) => a.groupId === g.id && isAccountActive(a))
  );

  const resolvedTemplate = useMemo(() => {
    const sel = parseSelection(accountSelection);
    if (sel.type === "account") {
      const account = accountsList.find((a: any) => a.id === sel.id);
      return account?.effectiveTemplate ?? account?.propFirmTemplate ?? account?.group?.propFirmTemplate ?? null;
    }
    if (sel.type === "group") {
      const group = groupsList.find((g: any) => g.id === sel.id);
      return group?.propFirmTemplate ?? null;
    }
    return null;
  }, [accountSelection, accountsList, groupsList]);

  // Commissions seules, fusion firm × palier VIVANT faite côté serveur
  // (effectiveCommissions, référence stable du cache de query : les effets
  // qui en dépendent ne rebouclent pas). `resolvedTemplate` continue de
  // porter le nom de la firm pour la ligne « depuis {name} ».
  const resolvedCommissions = useMemo(() => {
    const sel = parseSelection(accountSelection);
    if (sel.type === "account") {
      const account = accountsList.find((a: any) => a.id === sel.id);
      return account?.effectiveCommissions ?? resolvedTemplate;
    }
    return resolvedTemplate;
  }, [accountSelection, accountsList, resolvedTemplate]);

  useEffect(() => {
    if (resolvedCommissions?.commissionPerContract != null) {
      setCommissionPerTrade(String(resolvedCommissions.commissionPerContract));
    }
  }, [resolvedCommissions]);

  useEffect(() => {
    if (!resolvedCommissions || !manualForm.symbol) return;
    const rate = resolveCommissionForSymbol(manualForm.symbol, resolvedCommissions);
    if (rate !== null) {
      setManualForm(prev => ({ ...prev, commission: String(-Math.abs(rate)) }));
    }
  }, [manualForm.symbol, resolvedCommissions]);

  const handleClose = () => {
    onOpenChange(false);
    setStep("choose");
    setPasteText("");
    setParsedTrades([]);
    setParseError(null);
    setUnmatchedFills(0);
    setAccountSelection("none");
    setManualForm(initialManualForm);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Accepts the paste parsers' `T[] | null` and the CSV parser's discriminated
  // result. `null` (paste failure) maps to the generic "format" error.
  const applyParsedResult = useCallback(
    (result: ParsedTradovateTrade[] | null | TradovateParseResult) => {
      if (result === null) {
        setParseError("format");
        setParsedTrades([]);
      } else if (Array.isArray(result)) {
        setParsedTrades(result);
        setParseError(null);
      } else if (result.ok) {
        setParsedTrades(result.trades);
        setParseError(null);
      } else {
        setParseError(result.reason);
        setParsedTrades([]);
      }
    },
    [],
  );

  const handlePasteChange = useCallback((text: string) => {
    setPasteText(text);
    setParseError(null);
    setParsedTrades([]);
    if (!text.trim()) return;
    applyParsedResult(parseTradovatePaste(text));
  }, [applyParsedResult]);

  const handleTopstepXPasteChange = useCallback((text: string) => {
    setPasteText(text);
    setParseError(null);
    setParsedTrades([]);
    setUnmatchedFills(0);
    if (!text.trim()) return;

    // Try the vertical 12-line format first, then the tabbed-fills format.
    const vertical = parseTopstepXPaste(text);
    if (vertical) {
      applyParsedResult(vertical);
      return;
    }
    const fills = parseTopstepXFillsPaste(text);
    if (fills) {
      setUnmatchedFills(fills.unmatchedCount);
      // The format was recognised. If it yielded complete trades, apply them.
      // If it yielded none (paste holds only opening fills), don't raise a
      // "format" error — the amber unmatched-fills warning already explains it.
      if (fills.trades.length > 0) {
        applyParsedResult(fills.trades);
      }
      return;
    }
    applyParsedResult(null);
  }, [applyParsedResult]);

  const handleCSVFile = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = (e.target?.result as string) ?? "";
      applyParsedResult(parseTradovateCSV(text));
      setPasteText("");
    };
    reader.onerror = () => {
      setParseError("empty");
      setParsedTrades([]);
      setPasteText("");
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [applyParsedResult]);

  // Returns the list of accountIds to import to (1 for account, N for group, 0 for none)
  const getTargetAccountIds = (): (string | null)[] => {
    const sel = parseSelection(accountSelection);
    if (sel.type === "account") return [sel.id];
    if (sel.type === "group") {
      const groupAccounts = accountsList.filter((a: any) => a.groupId === sel.id);
      return groupAccounts.length > 0 ? groupAccounts.map((a: any) => a.id) : [null];
    }
    return [null];
  };

  const handleImport = async () => {
    if (parsedTrades.length === 0) return;

    // Drop rows the server schema would reject (non-positive price/qty, NaN pnl,
    // invalid dates). One bad row otherwise makes importTradesFromCSV throw a 500,
    // which Wasp surfaces as the blank "error rendering this page" screen.
    const { valid: cleanTrades, skipped } = sanitizeParsedTrades(parsedTrades);
    if (cleanTrades.length === 0) {
      toast({
        title: t.session.tradovateImportFailed,
        description: t.journal.importAllRowsInvalid,
        variant: "destructive",
      });
      return;
    }
    if (skipped > 0) {
      toast({
        title: t.journal.importSomeRowsSkipped.replace("{count}", String(skipped)),
      });
    }

    setIsImporting(true);
    const targetIds = getTargetAccountIds();
    try {
      let totalImported = 0;
      let totalDuplicates = 0;
      for (const accountId of targetIds) {
        const trades = cleanTrades.map((tr) => {
          const symbolRate = resolvedCommissions
            ? resolveCommissionForSymbol(tr.symbol, resolvedCommissions)
            : null;
          const fallbackRate = commissionPerTrade !== "" ? parseFloat(commissionPerTrade) || 0 : 0;
          const rate = symbolRate ?? fallbackRate;

          return {
            date: tr.entryTime,
            exitDate: tr.exitTime,
            symbol: tr.symbol,
            type: tr.type,
            status: "closed" as const,
            entryPrice: tr.entryPrice,
            exitPrice: tr.exitPrice,
            quantity: tr.qty,
            pnl: tr.pnl,
            commission: tr.commission !== undefined
              ? tr.commission
              : -Math.abs(rate) * tr.qty,
            accountId,
          };
        });
        const result = await importTradesFn({ trades });
        totalImported += result.imported;
        totalDuplicates += result.duplicates ?? 0;
        handleViolations((result as any)?.accountViolations);
      }
      toast({
        title: totalImported === 0 && totalDuplicates > 0
          ? t.journal.importNothingNew
          : `${totalImported} ${t.session.tradovateImportDone}`,
        description: totalDuplicates > 0
          ? t.journal.importDuplicatesSkipped.replace("{count}", String(totalDuplicates))
          : undefined,
      });
      onImportDone();
      handleClose();
    } catch (error: any) {
      toast({
        title: t.session.tradovateImportFailed,
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleManualSubmit = async () => {
    if (!manualForm.symbol || !manualForm.entryPrice || !manualForm.quantity) return;
    setIsSubmittingManual(true);
    const sel = parseSelection(accountSelection);
    const targetIds: (string | null)[] = sel.type === "group"
      ? accountsList.filter((a: any) => a.groupId === sel.id).map((a: any) => a.id as string)
      : [sel.type === "account" ? sel.id : null];

    try {
      let detectedViolation: AccountViolation | null = null;

      for (const accountId of targetIds) {
        const result = await createTradeFn({
          date: new Date(manualForm.date),
          symbol: manualForm.symbol.toUpperCase().trim(),
          type: manualForm.type,
          status: manualForm.status,
          entryPrice: parseFloat(manualForm.entryPrice),
          exitPrice: manualForm.exitPrice ? parseFloat(manualForm.exitPrice) : null,
          quantity: parseFloat(manualForm.quantity),
          pnl: manualForm.pnl ? parseFloat(manualForm.pnl) : null,
          commission: parseFloat(manualForm.commission) || 0,
          notes: manualForm.notes || null,
          accountId,
          tagIds: manualForm.tagIds,
          customFields: [],
        });

        const violation = (result as { accountViolation?: AccountViolation }).accountViolation;
        if (violation) {
          detectedViolation = violation;
          break;
        }
      }

      if (detectedViolation) {
        handleViolation(detectedViolation);
        // Do not show success toast nor close the modal — user must respond to the violation modal first
      } else {
        toast({ title: t.journal.tradeAdded, description: t.journal.tradeAddedDescription });
        onImportDone();
        handleClose();
      }
    } catch (error: any) {
      toast({
        title: t.journal.cannotAddTrade,
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmittingManual(false);
    }
  };

  const longs = parsedTrades.filter((t) => t.type === "long").length;
  const shorts = parsedTrades.filter((t) => t.type === "short").length;
  // Les trades Tradovate arrivent sans commission : elle est dérivée du taux de
  // la modale au moment de l'import. Un aperçu qui lisait `commission ?? 0`
  // ignorait donc les frais et annonçait +0.00 pour une session à -60.
  const totalPnl = previewNetPnl(
    parsedTrades.map((tr) => ({
      pnl: tr.pnl,
      qty: tr.qty,
      commission: tr.commission,
      symbol: tr.symbol,
    })),
    commissionPerTrade !== "" ? parseFloat(commissionPerTrade) || 0 : 0,
    // Même priorité qu'à l'import : le taux du template prime sur celui saisi.
    (symbol) => (resolvedCommissions ? resolveCommissionForSymbol(symbol, resolvedCommissions) : null),
  );

  const sel = parseSelection(accountSelection);
  const isGroupSelected = sel.type === "group";
  const groupAccountCount = isGroupSelected
    ? accountsList.filter((a: any) => a.groupId === sel.id).length
    : 0;

  // ── Per-import-source account/group eligibility ───────────────────────────
  // - topstepx: only Topstep accounts/groups
  // - tradovate: everything except Topstep accounts and MetaTrader (MetaAPI) accounts;
  //   a group is excluded if it is a Topstep group or contains a MetaTrader account
  const accountAllowedForStep = useCallback(
    (a: any): boolean => {
      if (step === "topstepx") return isTopstepAccount(a);
      if (step === "tradovate") return !isTopstepAccount(a) && !isMetaTraderAccount(a);
      return true;
    },
    [step],
  );

  const groupAllowedForStep = useCallback(
    (g: any): boolean => {
      const groupAccounts = accountsList.filter((a: any) => a.groupId === g.id);
      if (step === "topstepx") {
        return isTopstepGroup(g) || groupAccounts.some(isTopstepAccount);
      }
      if (step === "tradovate") {
        if (isTopstepGroup(g)) return false;
        return !groupAccounts.some((a: any) => isTopstepAccount(a) || isMetaTraderAccount(a));
      }
      return true;
    },
    [step, accountsList],
  );

  const visibleAccountsList = accountsList.filter(accountAllowedForStep);
  const visibleGroupsList = groupsList.filter(groupAllowedForStep);

  // At least one Topstep account or group exists → Topstep X import is usable
  const hasTopstepTarget =
    accountsList.some(isTopstepAccount) ||
    groupsList.some((g: any) =>
      isTopstepGroup(g) || accountsList.some((a: any) => a.groupId === g.id && isTopstepAccount(a)),
    );

  // Clear the selection when switching steps so a now-ineligible account/group
  // can't carry over (e.g. a Topstep account selected then switching to Tradovate).
  useEffect(() => {
    if (step === "choose") return;
    if (accountSelection === "none") return;
    const cur = parseSelection(accountSelection);
    const stillVisible =
      (cur.type === "account" && visibleAccountsList.some((a: any) => a.id === cur.id)) ||
      (cur.type === "group" && visibleGroupsList.some((g: any) => g.id === cur.id));
    if (!stillVisible) setAccountSelection("none");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // ── Account/Group selector (shared between steps, filtered per source) ─────
  const AccountGroupSelector = (
    <div className="space-y-1.5">
      <Label>
        {step === "topstepx" ? t.journal.selectTopstepAccountOrGroup : t.journal.selectAccountOrGroup}
      </Label>
      <Select value={accountSelection} onValueChange={setAccountSelection}>
        <SelectTrigger>
          <SelectValue placeholder={t.journal.selectAccountOrGroupPlaceholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">{t.journal.noAccount}</SelectItem>
          {visibleGroupsList.length > 0 && (
            <>
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                {t.journal.groupPrefix}
              </div>
              {visibleGroupsList.map((group: any) => (
                <SelectItem key={`group:${group.id}`} value={`group:${group.id}`}>
                  {group.name}
                </SelectItem>
              ))}
            </>
          )}
          {visibleAccountsList.length > 0 && (
            <>
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                {t.journal.account}
              </div>
              {visibleAccountsList.map((account: any) => (
                <SelectItem key={`account:${account.id}`} value={`account:${account.id}`}>
                  {account.name}
                </SelectItem>
              ))}
            </>
          )}
        </SelectContent>
      </Select>
      {step === "topstepx" && visibleAccountsList.length === 0 && visibleGroupsList.length === 0 && (
        <p className="text-xs text-muted-foreground">{t.journal.noTopstepAccountAvailable}</p>
      )}
      {isGroupSelected && groupAccountCount > 0 && (
        <p className="text-xs text-muted-foreground">
          {t.journal.importingToGroup} ({groupAccountCount} comptes)
        </p>
      )}
    </div>
  );

  return (
    <>
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === "choose"
              ? t.journal.addTradeTitle
              : step === "tradovate"
              ? t.journal.tradovateImportTitle
              : step === "topstepx"
              ? t.journal.topstepxImportTitle
              : t.journal.manualTradeTitle}
          </DialogTitle>
          <DialogDescription>
            {step === "choose"
              ? t.journal.addTradeSubtitle
              : step === "tradovate"
              ? t.journal.tradovateImportSubtitle
              : step === "topstepx"
              ? t.journal.topstepxImportSubtitle
              : t.journal.manualTradeSubtitle}
          </DialogDescription>
        </DialogHeader>

        {/* ── Step: choose ── */}
        {step === "choose" && (
          <div className="grid grid-cols-1 gap-3 py-2">
            <button
              type="button"
              onClick={() => setStep("manual")}
              className="flex items-start gap-4 rounded-lg border border-border p-4 text-left transition-colors hover:border-primary hover:bg-primary/5"
            >
              <div className="mt-0.5 rounded-md bg-primary/10 p-2">
                <PenLine className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="font-semibold">{t.journal.methodManual}</div>
                <div className="text-sm text-muted-foreground mt-0.5">{t.journal.methodManualDescription}</div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setStep("tradovate")}
              className="flex items-start gap-4 rounded-lg border border-border p-4 text-left transition-colors hover:border-primary hover:bg-primary/5"
            >
              <BrandLogoChip src="/logos/tradovate.png" fallback="Tradovate" />
              <div>
                <div className="font-semibold">{t.journal.methodTradovate}</div>
                <div className="text-sm text-muted-foreground mt-0.5">{t.journal.methodTradovateDescription}</div>
              </div>
            </button>

            <button
              type="button"
              disabled={!hasTopstepTarget}
              onClick={() => setStep("topstepx")}
              className="flex items-start gap-4 rounded-lg border border-border p-4 text-left transition-colors hover:border-primary hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border disabled:hover:bg-transparent"
            >
              <BrandLogoChip src="/logos/topstepx.png" fallback="Topstep X" />
              <div>
                <div className="font-semibold">{t.journal.methodTopstepX}</div>
                <div className="text-sm text-muted-foreground mt-0.5">
                  {hasTopstepTarget ? t.journal.methodTopstepXDescription : t.journal.methodTopstepXNoAccount}
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => { handleClose(); onImportCSV(); }}
              className="flex items-start gap-4 rounded-lg border border-border p-4 text-left transition-colors hover:border-primary hover:bg-primary/5"
            >
              <div className="mt-0.5 rounded-md bg-primary/10 p-2">
                <Upload className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="font-semibold">{t.journal.methodCsvImport}</div>
                <div className="text-sm text-muted-foreground mt-0.5">{t.journal.methodCsvImportDescription}</div>
                {/* Les plateformes réellement acceptées par ce chemin, vérifiées
                    par csvImportAllFormats.test.ts. Le chip retombe sur son
                    libellé texte si le logo manque (cas de Quantower). */}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <BrandLogoChip src="/logos/tradovate.png" fallback="Tradovate" size="sm" />
                  <BrandLogoChip src="/logos/topstepx.png" fallback="Topstep X" size="sm" />
                  <BrandLogoChip src="/logos/quantower.png" fallback="Quantower" size="sm" />
                </div>
              </div>
            </button>
          </div>
        )}

        {/* ── Step: manual ── */}
        {step === "manual" && (
          <div className="space-y-3 py-2">
            {AccountGroupSelector}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t.journal.date}</Label>
                <Input
                  type="date"
                  value={manualForm.date}
                  onChange={(e) => setManualForm({ ...manualForm, date: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t.journal.symbol}</Label>
                <Input
                  value={manualForm.symbol}
                  onChange={(e) => setManualForm({ ...manualForm, symbol: e.target.value })}
                  placeholder="NQ1!, AAPL…"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t.journal.type}</Label>
                <Select value={manualForm.type} onValueChange={(v) => setManualForm({ ...manualForm, type: v as "long" | "short" })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="long">{t.journal.long}</SelectItem>
                    <SelectItem value="short">{t.journal.short}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t.journal.status}</Label>
                <Select value={manualForm.status} onValueChange={(v) => setManualForm({ ...manualForm, status: v as "open" | "closed" })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">{t.journal.open}</SelectItem>
                    <SelectItem value="closed">{t.journal.closed}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>{t.journal.entryPrice}</Label>
                <Input type="number" step="any" value={manualForm.entryPrice} onChange={(e) => setManualForm({ ...manualForm, entryPrice: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>{t.journal.exitPrice}</Label>
                <Input type="number" step="any" value={manualForm.exitPrice} onChange={(e) => setManualForm({ ...manualForm, exitPrice: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>{t.journal.quantity}</Label>
                <Input type="number" step="any" min="0" value={manualForm.quantity} onChange={(e) => setManualForm({ ...manualForm, quantity: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t.journal.pnl}</Label>
                <Input type="number" step="any" value={manualForm.pnl} onChange={(e) => setManualForm({ ...manualForm, pnl: e.target.value })} placeholder="0.00" />
              </div>
              <div className="space-y-1.5">
                <Label>{t.journal.commission}</Label>
                <Input type="number" step="any" value={manualForm.commission} onChange={(e) => setManualForm({ ...manualForm, commission: e.target.value })} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>{t.journal.notes}</Label>
              <Textarea
                value={manualForm.notes}
                onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })}
                rows={2}
                className="resize-none"
              />
            </div>

            <div className="space-y-2">
              <Label>{t.journal.tags}</Label>
              <TradeTagSelector
                selectedTagIds={manualForm.tagIds}
                onChange={(tagIds) => setManualForm({ ...manualForm, tagIds })}
              />
            </div>

            <div className="flex justify-between pt-1">
              <Button type="button" variant="ghost" size="sm" onClick={() => setStep("choose")}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                {t.common.back}
              </Button>
              <Button
                type="button"
                onClick={handleManualSubmit}
                disabled={!manualForm.symbol || !manualForm.entryPrice || !manualForm.quantity || isSubmittingManual}
              >
                {isSubmittingManual && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t.journal.createTrade}
              </Button>
            </div>
          </div>
        )}

        {/* ── Step: tradovate ── */}
        {step === "tradovate" && (
          <div className="space-y-4 py-2">
            {AccountGroupSelector}

            {/* Commission per trade */}
            <div className="space-y-1.5">
              <Label>{t.journal.commissionPerTrade}</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={commissionPerTrade}
                onChange={(e) => {
                  setCommissionPerTrade(e.target.value);
                  localStorage.setItem("tradovate_commission_per_trade", e.target.value);
                }}
                placeholder={t.journal.commissionPerTradePlaceholder}
              />
              {resolvedTemplate !== null && (
                <p className="text-xs text-muted-foreground">
                  {t.journal.commissionFromPropFirm.replace("{name}", resolvedTemplate?.name ?? "")}
                </p>
              )}
            </div>

            {/* Paste zone + CSV upload */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>{t.session.tradovateImport}</Label>
                <span className="text-xs text-muted-foreground">{t.session.tradovateOrUploadCSV}</span>
              </div>
              <Textarea
                value={pasteText}
                onChange={(e) => handlePasteChange(e.target.value)}
                placeholder={t.session.tradovatePastePlaceholder}
                rows={5}
                className="font-mono text-xs resize-none"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCSVFile(f); }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                {t.session.tradovateUploadCSV}
              </Button>
            </div>

            {/* Parse feedback */}
            {parsedTrades.length > 0 && (
              <div className="rounded-lg border border-green-500/30 bg-green-500/5 p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 font-medium">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>{parsedTrades.length} {t.session.tradovateParseSuccess}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                  <div><span className="text-green-500 font-semibold">{longs}</span> longs</div>
                  <div><span className="text-red-500 font-semibold">{shorts}</span> shorts</div>
                  <div>
                    <span className={`font-semibold ${totalPnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"}`}>
                      {totalPnl >= 0 ? "+" : ""}{totalPnl.toFixed(2)}
                    </span> P&L
                  </div>
                </div>
                {isGroupSelected && groupAccountCount > 1 && (
                  <p className="text-xs text-muted-foreground border-t pt-2">
                    × {groupAccountCount} = {parsedTrades.length * groupAccountCount} trades total
                  </p>
                )}
              </div>
            )}

            {parseError && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <XCircle className="h-4 w-4 flex-shrink-0" />
                <span>
                  {parseError === "empty"
                    ? t.session.tradovateParseErrorEmpty
                    : parseError === "no-trades"
                    ? t.session.tradovateParseErrorNoTrades
                    : t.session.tradovateParseErrorFormat}
                </span>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between pt-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => { setStep("choose"); setPasteText(""); setParsedTrades([]); setParseError(null); setUnmatchedFills(0); }}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                {t.common.back}
              </Button>
              <Button
                type="button"
                onClick={handleImport}
                disabled={parsedTrades.length === 0 || isImporting}
              >
                {isImporting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t.session.tradovateImportConfirm}
              </Button>
            </div>
          </div>
        )}

        {/* ── Step: topstepx ── */}
        {step === "topstepx" && (
          <div className="space-y-4 py-2">
            {AccountGroupSelector}

            {/* Paste zone */}
            <div className="space-y-1.5">
              <Label>{t.session.topstepxImport}</Label>
              <Textarea
                value={pasteText}
                onChange={(e) => handleTopstepXPasteChange(e.target.value)}
                placeholder={t.session.topstepxPastePlaceholder}
                rows={5}
                className="font-mono text-xs resize-none"
              />
            </div>

            {/* Parse feedback */}
            {parsedTrades.length > 0 && (
              <div className="rounded-lg border border-green-500/30 bg-green-500/5 p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 font-medium">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>{parsedTrades.length} {t.session.tradovateParseSuccess}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                  <div><span className="text-green-500 font-semibold">{longs}</span> longs</div>
                  <div><span className="text-red-500 font-semibold">{shorts}</span> shorts</div>
                  <div>
                    <span className={`font-semibold ${totalPnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"}`}>
                      {totalPnl >= 0 ? "+" : ""}{totalPnl.toFixed(2)}
                    </span> P&L
                  </div>
                </div>
                {isGroupSelected && groupAccountCount > 1 && (
                  <p className="text-xs text-muted-foreground border-t pt-2">
                    × {groupAccountCount} = {parsedTrades.length * groupAccountCount} trades total
                  </p>
                )}
              </div>
            )}

            {parseError === "format" && pasteText.trim() && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <XCircle className="h-4 w-4 flex-shrink-0" />
                <span>{t.session.topstepxParseError}</span>
              </div>
            )}

            {unmatchedFills > 0 && pasteText.trim() && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  {t.session.topstepxUnmatchedFillsWarning.replace("{count}", String(unmatchedFills))}
                </span>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between pt-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => { setStep("choose"); setPasteText(""); setParsedTrades([]); setParseError(null); setUnmatchedFills(0); }}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                {t.common.back}
              </Button>
              <Button
                type="button"
                onClick={handleImport}
                disabled={parsedTrades.length === 0 || isImporting || sel.type === "none"}
              >
                {isImporting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t.session.tradovateImportConfirm}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    <AccountBlewUpModal {...blewUpModalProps} />
    </>
  );
}
